import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CButton, CTooltip, CModal, CModalHeader, CModalTitle, CModalFooter, CModalBody, CFormSwitch, CCallout } from '@coreui/react'
import { DatePickerComponent } from '@syncfusion/ej2-react-calendars'
import FacilityCRUD from '../../services/general/getFacilityData.api'
import fetchPatientData from '../../services/general/patientGeneralAPI.api'
import { Info, LogOutIcon, Pencil, Search } from 'lucide-react'
import toast from 'react-hot-toast'
import UpdateSchedulerData from './UpdateSchedulerData'
import SavePatients from '../../services/general/savePatientinformation.api'
import { toastConfig } from '../../constants/other.config'
import SchedulerAccordian from '../../components/shared/SchedulerAccordian'
import PatientNoteDataCRUD from '../../services/general/patientNoteData.api'
import PatientAppointmentDetails from '../../services/general/appointmentData.api'
import { getAllAppointments, getAllAppointmentsDetails, getAppointmentDataID } from '../../components/shared/appointmentFilterData'
import SaveLeave from '../../services/general/EmployeeLeaveCrud.api'
import PatientInsuranceFn from '../../services/general/PatientInsuranceAPI.api'
import EditPatientDetails from '../../components/shared/EditPatientDetails'
import { CircularProgress } from '@mui/material'
import Pageloader from '../../components/shared/Pageloader'
import { useHistory, useLocation } from 'react-router-dom'
import { usePatientStatus } from '../../hooks/usecheckForUpdate'
import { fetchCheckForUpdate } from '../../components/shared/checkForUpdateApi'
import { formatToYYYYMMDD } from '../../utils/checkForUpdateUtils'
import { toLeaveCalendarKey } from '../../components/shared/leaveCalendarDateUtils'
import { SCHEDULER_HOLIDAY_APPOINTMENT_ERROR, getSchedulerHolidayCountry, isSchedulerHolidayDate } from './schedulerHolidayUtils'

import AppSelect from '../../components/shared/AppSelect'
import './dashboard.css'
import { nameMatchesSearch } from '../../utils/searchUtils'


const getSafeInteger = (value) => {
  const parsedValue = Number.parseInt(value, 10)
  return Number.isFinite(parsedValue) ? parsedValue : 0
}

const APPOINTMENT_ALREADY_CANCELLED_MESSAGE = 'Appointment has already been cancelled.'
const getAppointmentDateKey = (value) => {
  if (value instanceof Date) {
    const year = value.getFullYear()
    const month = String(value.getMonth() + 1).padStart(2, '0')
    const day = String(value.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const rawValue = String(value || '').trim()

  if (!rawValue) {
    return ''
  }

  const normalizedIsoDate = rawValue.split('T')[0].split(' ')[0]
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalizedIsoDate)) {
    return normalizedIsoDate
  }

  const slashDateMatch = normalizedIsoDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (slashDateMatch) {
    const [, month, day, year] = slashDateMatch
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
  }

  const parsedValue = new Date(rawValue)
  if (!Number.isNaN(parsedValue.getTime())) {
    const year = parsedValue.getFullYear()
    const month = String(parsedValue.getMonth() + 1).padStart(2, '0')
    const day = String(parsedValue.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  return normalizedIsoDate
}
const buildPatientDateKey = (patientId, scheduleDate) => `${String(patientId || '').trim()}::${getAppointmentDateKey(scheduleDate)}`
const buildPatientProviderDateKey = (patientId, providerId, scheduleDate) => `${String(patientId || '').trim()}::${String(providerId || '').trim()}::${getAppointmentDateKey(scheduleDate)}`

const PROVIDER_ON_LEAVE_MESSAGE = 'Selected Provider is on Leave.'
const PROVIDER_LEAVE_BLOCKING_STATUSES = new Set(['approved', 'active'])
const WEEKEND_APPOINTMENT_MESSAGE = "Appointment cannot be scheduled on Weekends"
const SCHEDULED_STATUSES = new Set(['Scheduled', 'Rescheduled'])
const SchedulerMap = lazy(() => import('./SchedulerMap'))

const getCompOffDateKeys = (leave = {}) => {
  const compOffDates = leave?.CompOffDates || leave?.compOffDates || leave?.CompensationDate || leave?.compensationDate || ''
  const values = Array.isArray(compOffDates) ? compOffDates : String(compOffDates).split(',')

  return values
    .map((date) => toLeaveCalendarKey(typeof date === 'string' ? date.trim() : date))
    .filter(Boolean)
}

const isWeekendAppointmentAllowed = (selectedDate, selectedProvider, compOffLeaves = []) => {
  const appointmentDateKey = toLeaveCalendarKey(selectedDate)
  if (!appointmentDateKey) return true

  const [year, month, day] = appointmentDateKey.split('-').map(Number)
  const dayOfWeek = new Date(year, month - 1, day).getDay()
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6

  if (!isWeekend) return true

  const providerId = selectedProvider?.value || selectedProvider?.ID || selectedProvider?.id
  if (!providerId) return false

  return compOffLeaves.some((leave) => {
    const leaveStatus = String(leave?.Status || leave?.status || '').trim().toLowerCase()
    const hasCompOffDates = getCompOffDateKeys(leave)
    const isCompOff = Boolean(leave?.IsCompOff || leave?.isCompOff) ||
      String(leave?.LeaveType || leave?.leaveType || '').trim().toLowerCase() === 'comp off' ||
      hasCompOffDates.length > 0

    return isCompOff &&
      PROVIDER_LEAVE_BLOCKING_STATUSES.has(leaveStatus) &&
      hasCompOffDates.includes(appointmentDateKey)
  })
}

const normalizeProviderIdentity = (value) => String(value || '').replace(/\s+/g, '').trim().toLowerCase()

const getProviderIdentityValues = (provider = {}) => [
  provider?.value,
  provider?.ID,
  provider?.id,
  provider?.EmployeeId,
  provider?.employeeId,
  provider?.UserId,
  provider?.userId,
  provider?.Username,
  provider?.UserName,
  provider?.username,
  provider?.userName,
  provider?.Name,
  provider?.name,
  provider?.label,
]
  .map(normalizeProviderIdentity)
  .filter(Boolean)

let cachedSchedulerFacilities = null
let cachedSchedulerFacilitiesPromise = null
let cachedSchedulerPatients = null
let cachedSchedulerPatientsPromise = null
let cachedSchedulerProviders = null
let cachedSchedulerProvidersPromise = null
let cachedSchedulerCheckForUpdate = null
let cachedSchedulerCheckForUpdatePromise = null
const schedulerProviderDetailsCache = new Map()
const schedulerProviderDetailsPromiseCache = new Map()

const buildAppointmentDetails = (appointmentGroups = []) =>
  appointmentGroups.flatMap((group) => group?.appointmentDetails || []).filter((item) => item && Object.keys(item).length > 0)

const buildScheduledAppointments = (appointmentDetails = []) => appointmentDetails.filter((item) => SCHEDULED_STATUSES.has(item?.data?.Status))
const hasValidNoteId = (noteId) => noteId !== null && noteId !== undefined && String(noteId).trim() !== ''

const getAppointmentSortTimestamp = (appointment = {}) => {
  const startTimeValue = appointment?.data?.StartTime || appointment?.data?.ScheduleDate || ''
  const createdDateValue = appointment?.data?.CreatedDate || ''
  const parsedStartTime = Date.parse(startTimeValue)

  if (Number.isFinite(parsedStartTime)) {
    return parsedStartTime
  }

  const parsedCreatedDate = Date.parse(createdDateValue)

  if (Number.isFinite(parsedCreatedDate)) {
    return parsedCreatedDate
  }

  const dateKey = getAppointmentDateKey(startTimeValue)
  const parsedDateKey = Date.parse(dateKey)

  return Number.isFinite(parsedDateKey) ? parsedDateKey : 0
}

const findMostRecentCanceledAppointmentWithNote = (appointmentEntries = [], patientId) => {
  const normalizedPatientId = String(patientId || '').trim()

  if (!normalizedPatientId) {
    return null
  }

  return appointmentEntries.reduce((latestAppointment, appointment) => {
    const appointmentPatientId = String(appointment?.data?.PatientID || appointment?.current?.Patient?.value || '').trim()
    const appointmentStatus = String(appointment?.data?.Status || '').trim().toLowerCase()

    if (appointmentPatientId !== normalizedPatientId || appointmentStatus !== 'canceled' || !hasValidNoteId(appointment?.data?.NoteID)) {
      return latestAppointment
    }

    if (!latestAppointment) {
      return appointment
    }

    return getAppointmentSortTimestamp(appointment) >= getAppointmentSortTimestamp(latestAppointment) ? appointment : latestAppointment
  }, null)
}

const reuseCanceledAppointmentNoteIds = (newAppointments = [], existingAppointments = []) => {
  const assignedPatients = new Set()

  return newAppointments.map((appointment) => {
    const patientId = String(appointment?.data?.PatientID || appointment?.current?.Patient?.value || '').trim()
    const existingNoteId = appointment?.data?.NoteID

    if (!patientId) {
      return appointment
    }

    if (hasValidNoteId(existingNoteId)) {
      assignedPatients.add(patientId)
      return appointment
    }

    if (assignedPatients.has(patientId)) {
      return appointment
    }

    const canceledAppointment = findMostRecentCanceledAppointmentWithNote(existingAppointments, patientId)

    if (!canceledAppointment) {
      return appointment
    }

    assignedPatients.add(patientId)

    return {
      ...appointment,
      data: {
        ...appointment.data,
        NoteID: canceledAppointment.data.NoteID,
      },
    }
  })
}

const SchedulePatient2 = () => {
  const location = useLocation()
  const history = useHistory()
  const [notSchedule, setNotSchedule] = useState([])
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [selectedProvider, setSelectedProvider] = useState(null)
  const [notScheduledFacility, setNotScheduledFacility] = useState({})
  const [addedInsurances, setAddedInsurances] = useState([])
  const [facilities, setFacilities] = useState([])
  const [locations, setLocations] = useState([])
  const [facilityByType, setFacilityByType] = useState({})
  const [providers, setProviders] = useState([])
  const [searchedFacilityData, setSearchedFacilityData] = useState({})
  const [selectedPatients, setSelectedPatients] = useState([])
  const [isPageLoading, setIsPageLoading] = useState(false)
  const [appointments, setAppointments] = useState([])
  const [patientData, setPatientData] = useState([])
  const [isProviderLoading, setIsProviderLoading] = useState(false)
  const [accordionState, setAccordionState] = useState([])
  const [onlyScheduled, setOnlyScheduled] = useState([])
  const [allAppointment, setAllAppointment] = useState([])
  const [loader, setLoader] = useState(false)
  const [showPatientInfo, setShowPatientInfo] = useState(false)
  const [patientInfo, setPatientInfo] = useState({})
  const [availableProviderForSelFacility, setAvailableProviderForSelFacility] = useState([])
  const [showAvailableProvider, setShowAvailableProvider] = useState(false)
  const [toggleStateFacility, setToggleStateFacility] = useState(false)
  const [originalAppointments, setOriginalAppointments] = useState([])
  const [toggleStatePatients, setToggleStatePatients] = useState(false)
  const { addPatient, appendPatientInTodaysList } = usePatientStatus()
  const initialLoadCompleteRef = useRef(false)
  const providerLeaveCacheRef = useRef(new Map())
  const createAppointmentInFlightRef = useRef(false)

  const invalidateSchedulerPatientCache = useCallback(() => {
    cachedSchedulerPatients = null
    cachedSchedulerPatientsPromise = null
  }, [])

  const patientSelectedFacility = async (rowSelected) => {
    try {
      const userFacilities = facilities.find((item) => item.label === rowSelected?.FacilityName)

      // setSelectedFacility(userFacilities ? userFacilities : facilities[0]);
      return userFacilities ? userFacilities : facilities[0]
    } catch (e) { }
  }
  const [dischargingPatient, setDischargingPatient] = useState(null)
  const saveDiagnosisData = async (updateData) => {
    try {
      const patientID = updateData?.PatientID
      const customData = {
        ...updateData,
        Status: 'Discharge',
      }
      let a = {}
      let facility = await patientSelectedFacility(customData)

      try {
        a['FacilityID'] = facility?.value?.toString() || ''
        a['FacilityName'] = facility?.label?.toString() || ''
        a['Room'] = customData?.Room?.toString() || '-1'
      } catch (e) {
        a['FacilityID'] = ''
        a['FacilityName'] = ''
        a['Room'] = -1
      }

      let allDiagnosis = customData?.Diagnosis || ''

      let insurances = addedInsurances.map((ins) => ({
        InsuranceID: ins?.ID,
      }))

      let savePatientInfo
      if (patientID && patientID !== '') {
        savePatientInfo = SavePatients('Update', JSON.stringify(customData), JSON.stringify(a), allDiagnosis, patientID, JSON.stringify(insurances))
      }

      toast.success('Patient discharged successfully.', toastConfig)
      invalidateSchedulerPatientCache()
      await getData(toggleStatePatients, selectedDate, { forcePatients: true })
    } catch (error) {
      toast.error('An error occurred while discharging the patient. Please try again.', toastConfig)
    }
  }
  const handleDischargePatient = (patient) => {
    saveDiagnosisData(dischargingPatient)
    setDischargingPatient(null)
  }

  const getCheckForUpdateData = useCallback(async ({ force = false } = {}) => {
    if (!force && cachedSchedulerCheckForUpdate) {
      return cachedSchedulerCheckForUpdate
    }

    if (!force && cachedSchedulerCheckForUpdatePromise) {
      return cachedSchedulerCheckForUpdatePromise
    }

    cachedSchedulerCheckForUpdatePromise = (async () => {
      try {
        const apiData = await fetchCheckForUpdate()
        cachedSchedulerCheckForUpdate = Array.isArray(apiData) ? apiData : []
        return cachedSchedulerCheckForUpdate
      } finally {
        cachedSchedulerCheckForUpdatePromise = null
      }
    })()

    return cachedSchedulerCheckForUpdatePromise
  }, [])

  const getCheckForUpdateEntryForDate = useCallback(
    async (date, options = {}) => {
      const apiData = await getCheckForUpdateData(options)
      const dateKey = formatToYYYYMMDD(date)
      return apiData?.find((item) => item?.JsonData?.[dateKey]) || null
    },
    [getCheckForUpdateData],
  )

  const handleDateChange = useCallback((event) => {
    setLoader(true)
    setSelectedDate(event?.value || null)
  }, [])

  const handleAllAppointmentsData = useCallback(async (type) => {
    const appointmentData = await getAllAppointments(type)
    const appointmentDetails = buildAppointmentDetails(appointmentData)
    const scheduleAppointment = buildScheduledAppointments(appointmentDetails)
    setAllAppointment(appointmentData)
    setOriginalAppointments(appointmentData)
    setAppointments(appointmentDetails)
    setOnlyScheduled(scheduleAppointment)
    return { appointmentData, appointmentDetails, scheduleAppointment }
  }, [])

  const getData = useCallback(async (showAll, byDate, options = {}) => {
    const { forcePatients = false } = options
    setLoader(true)
    try {
      const [{ appointmentData, appointmentDetails, scheduleAppointment }, patients] = await Promise.all([
        handleAllAppointmentsData('GET'),
        getPatients({ force: forcePatients }),
      ])

      const selectedDateKey = getAppointmentDateKey(byDate)
      const scheduledPatientIds = new Set(
        scheduleAppointment
          .filter((item) => !selectedDateKey || getAppointmentDateKey(item?.data?.StartTime) === selectedDateKey)
          .map((item) => String(item?.data?.PatientID || '').trim())
          .filter(Boolean),
      )
      setAllAppointment(appointmentData)
      setOriginalAppointments(appointmentData)
      setAppointments(appointmentDetails)
      setOnlyScheduled(scheduleAppointment)
      setNotSchedule(
        showAll
          ? patients
          : patients.filter((item) => {
            const patientId = String(item?.PatientID || '').trim()
            return patientId && !scheduledPatientIds.has(patientId)
          }),
      )
    } finally {
      setLoader(false)
    }
  }, [handleAllAppointmentsData])

  const getPatients = useCallback(async ({ force = false } = {}) => {
    if (!force && cachedSchedulerPatients) {
      setPatientData(cachedSchedulerPatients)
      return cachedSchedulerPatients
    }

    if (!force && cachedSchedulerPatientsPromise) {
      const patientList = await cachedSchedulerPatientsPromise
      setPatientData(patientList)
      return patientList
    }

    cachedSchedulerPatientsPromise = (async () => {
      const response = await SavePatients('GetAll', '', '', '')
      const parsedResponse = JSON.parse(response.d)
      const patientList = (parsedResponse?.Data || []).filter((item) => item.Status !== 'Discharge')
      cachedSchedulerPatients = patientList
      return patientList
    })()

    try {
      const patientList = await cachedSchedulerPatientsPromise
      setPatientData(patientList)
      return patientList
    } finally {
      cachedSchedulerPatientsPromise = null
    }
  }, [])
  const [createAppointmentLoader, setCreateAppointmentLoader] = useState(false)
  const handleSchedule = async () => {
    if (createAppointmentInFlightRef.current) return

    createAppointmentInFlightRef.current = true
    setCreateAppointmentLoader(true)
    try {
      if (!selectedProvider || !selectedDate) {
        alert('Please select a provider and date before scheduling.')
        return
      }

      const scheduledData = selectedPatients.map((patient) => ({
        patient: patient.patient,
        selectedProvider: patient.selectedProvider,
        selectedDate: patient.selectedDate,
        scheduledDate: patient.selectedDate,
         Insurance:
        patient.Insurance ||
        patient.patient?.Insurance ||
        patient.patient?.insurance ||
        {},

      otherDetails: patient.otherDetails || {},
        isFacility: true,
      }))

      await handleManualScheduleData(scheduledData)
    } catch (error) {
      console.error("error", error)
    } finally {
      createAppointmentInFlightRef.current = false
      setCreateAppointmentLoader(false)
    }



    // You can then submit this `scheduledData` to an API or handle further.
  }

  // const handleScheduleAppointment = (props, patient, oldData) => {
  //   let day = ''
  //   let startTime = new Date(props.StartTime)
  //   let endTime = new Date(props.EndTime)
  //   const incrementDay = (currentDate) => {
  //     const nextDate = new Date(currentDate)
  //     nextDate.setDate(nextDate.getDate() + 1) // Increment day by 1
  //     return nextDate
  //   }
  //   let counter = 0

  //   while (Object.keys(props).length > 0) {
  //     const dayOptions = { weekday: 'long' }
  //     day = startTime.toLocaleDateString('en-US', dayOptions)

  //     try {
  //       const allDayData = daysMap[day]

  //       if (!allDayData) {
  //         console.warn(`No data found for ${day}. Moving to the next day.`)
  //         startTime = incrementDay(startTime)
  //         continue // Skip the rest of the logic and retry for the next day
  //       }
  //       console.log('allDayData before and after ', allDayData, locations, daysMap)
  //       allDayData.map((obj) => {
  //         obj.Facilities.map((facility) => {
  //           let tempFacility = locations.find((o) => o.ID == facility.FacilityID)
  //           console.log('facilityfacilityfacility', facility, tempFacility)
  //           facility['Name'] = tempFacility.Name
  //           facility['Type'] = tempFacility.Type
  //           return facility
  //         })
  //         return obj
  //       })

  //       const providerInfo = allDayData.filter((obj) => obj.Facilities.some((facility) => facility.Name === patient.FacilityName))

  //       if (providerInfo.length === 0) {
  //         console.warn(`No data for ${day}. Moving to the next day.`)
  //         startTime = incrementDay(startTime)
  //       } else {
  //         // allData.current.Provider = {
  //         //     value: providerInfo[0].ID,
  //         //     label: providerInfo[0].Name,
  //         // };

  //         let provider = providerInfo.filter((item) => item?.ID === oldData?.current?.Provider?.value)

  //         if (provider.length === 0) {
  //           provider = providerInfo[0]
  //         } else {
  //           provider = provider[0]
  //         }

  //         return startTime
  //       }
  //       counter++
  //       if (counter > 7) {
  //         alert(`No providers are currently assigned to this facility for patient ${props?.PatientName || ''}. Scheduling cannot proceed at this time.`)
  //         break
  //       }
  //     } catch (e) {
  //       console.log('errorrr', e)
  //       console.error('Error processing schedule:', e)
  //       return
  //     }
  //   }
  // }

  const handleScheduleAppointment = (props, patient, oldData) => {
    let startTime = new Date(props.StartTime)

    try {
      return startTime
    } catch (e) {
      console.error('Error processing schedule:', e)
      return
    }
  }

  const getFacilities = useCallback(async ({ force = false } = {}) => {
    if (!force && cachedSchedulerFacilities) {
      setFacilities(cachedSchedulerFacilities.formattedData)
      setFacilityByType(cachedSchedulerFacilities.groupedByType)
      setLocations(cachedSchedulerFacilities.validFacilities)
      return cachedSchedulerFacilities.rawData
    }

    if (!force && cachedSchedulerFacilitiesPromise) {
      const cachedData = await cachedSchedulerFacilitiesPromise
      setFacilities(cachedData.formattedData)
      setFacilityByType(cachedData.groupedByType)
      setLocations(cachedData.validFacilities)
      return cachedData.rawData
    }

    cachedSchedulerFacilitiesPromise = (async () => {
      const response = await FacilityCRUD('SELECT', {})
      const parsedResponse = JSON.parse(response.d)
      const rawData = parsedResponse?.Data || []
      const formattedData = rawData.map((facility) => ({
        value: facility.ID,
        label: facility.Name,
      }))
      const groupedByType = rawData.reduce((acc, facility) => {
        const type = facility.Type || 'Unknown'
        if (!acc[type]) acc[type] = []
        acc[type].push(facility)
        return acc
      }, {})

      cachedSchedulerFacilities = {
        rawData,
        formattedData,
        validFacilities: rawData,
        groupedByType,
      }

      return cachedSchedulerFacilities
    })()

    try {
      const cachedData = await cachedSchedulerFacilitiesPromise
      setFacilities(cachedData.formattedData)
      setFacilityByType(cachedData.groupedByType)
      setLocations(cachedData.validFacilities)
      return cachedData.rawData
    } finally {
      cachedSchedulerFacilitiesPromise = null
    }
  }, [])

  const getAllProviderDetails = useCallback(async (providerSummary) => {
    const providerId = providerSummary?.ID

    if (!providerId) return providerSummary

    if (schedulerProviderDetailsCache.has(providerId)) {
      return {
        ...providerSummary,
        ...schedulerProviderDetailsCache.get(providerId),
      }
    }

    if (schedulerProviderDetailsPromiseCache.has(providerId)) {
      const cachedDetails = await schedulerProviderDetailsPromiseCache.get(providerId)
      return {
        ...providerSummary,
        ...cachedDetails,
      }
    }

    const requestPromise = (async () => {
      const providerResponse = await fetchPatientData('SELECTALL', 'Provider', JSON.stringify({ ID: providerId }))
      const parsedResponse = JSON.parse(providerResponse.d || '{}')
      const providerDetails = {
        Insurances: parsedResponse?.Insurance || [],
        Facilities: parsedResponse?.Facility || [],
      }
      schedulerProviderDetailsCache.set(providerId, providerDetails)
      return providerDetails
    })()

    schedulerProviderDetailsPromiseCache.set(providerId, requestPromise)

    try {
      const providerDetails = await requestPromise
      return {
        ...providerSummary,
        ...providerDetails,
      }
    } finally {
      schedulerProviderDetailsPromiseCache.delete(providerId)
    }
  }, [])

  const getProviders = useCallback(async ({ force = false } = {}) => {
    try {
      if (!force && cachedSchedulerProviders) {
        setProviders(cachedSchedulerProviders)
        return cachedSchedulerProviders
      }

      if (!force && cachedSchedulerProvidersPromise) {
        const providerList = await cachedSchedulerProvidersPromise
        setProviders(providerList)
        return providerList
      }

      cachedSchedulerProvidersPromise = (async () => {
        const providerResponse = await fetchPatientData('SELECT', 'Provider', '{}')
        const parsedResponse = JSON.parse(providerResponse?.d || '{}')
        const providerList = Array.isArray(parsedResponse?.Data) ? parsedResponse.Data : []
        const enrichedProviders = await Promise.all(providerList.map((provider) => getAllProviderDetails(provider)))
        cachedSchedulerProviders = enrichedProviders
        return enrichedProviders
      })()

      const providerList = await cachedSchedulerProvidersPromise
      setProviders(providerList)
      return providerList
    } catch (err) {
      console.error('Error in getProviders:', err)
      return []
    } finally {
      cachedSchedulerProvidersPromise = null
    }
  }, [getAllProviderDetails])

  const availableProviders = useMemo(() => {
    if (!selectedDate) return []

    return [...providers].sort((a, b) => String(a?.Name || '').localeCompare(String(b?.Name || ''), undefined, { sensitivity: 'base' }))
  }, [providers, selectedDate])

  const handleSearch = () => {
    setSearchedFacilityData(facilityByType)
  }

  // const getProviders = async () => {
  //   let providerData = await fetchPatientData('SELECT', 'Provider', '{}')
  //   let data = JSON.parse(providerData.d)

  //   providerData = data.Data
  //   let newProviders = []
  //   for (let i = 0; i < providerData.length; i++) {
  //     let c = await getAllProviderDetails(providerData[i])
  //     newProviders.push(c)
  //   }
  //   setProviders(newProviders)

  //   const daysMap = {}
  //   const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

  //   newProviders.forEach((provider) => {
  //     let parsedCapacity = null
  //     try {
  //       if (provider?.ProviderData && provider.ProviderData !== 'undefined') {
  //         parsedCapacity = JSON.parse(provider.ProviderData)
  //       }
  //     } catch (error) {
  //       console.error('Invalid JSON in ProviderData:', provider.ProviderData, error)
  //       parsedCapacity = null
  //     }

  //     // Put the provider under each day (with all facilities)
  //     allDays.forEach((day) => {
  //       if (!daysMap[day]) daysMap[day] = []

  //       // Check if provider already exists for this day
  //       let providerEntry = daysMap[day].find((p) => p.ID === provider.ID)

  //       if (!providerEntry) {
  //         providerEntry = {
  //           ID: provider.ID,
  //           Name: provider.Name,
  //           Gender: provider.Gender,
  //           DOB: provider.DOB,
  //           Address: provider.Address,
  //           AddressDetails: provider.AddressDetails,
  //           Lat: provider.Lat,
  //           Lng: provider.Lng,
  //           Insurances: provider.Insurances,
  //           Facilities: [], // 👈 we'll add facilities below
  //           Capacity: parsedCapacity,
  //         }
  //         daysMap[day].push(providerEntry)
  //       }

  //       // Add all facilities (no filtering by day anymore)
  //       provider.Facilities.forEach((facility) => {
  //         providerEntry.Facilities.push(facility)
  //       })
  //     })
  //   })

  //   setDaysMap(daysMap)
  //   return newProviders
  // }

  const getDayName = (date) => {
    return date?.toLocaleDateString('en-US', { weekday: 'long' }) || ''
  }

  function formatDate(date) {
    const mm = String(date.getMonth() + 1).padStart(2, '0')
    const dd = String(date.getDate()).padStart(2, '0')
    const yyyy = String(date.getFullYear())
    const hh = String(date.getHours()).padStart(2, '0')
    const min = String(date.getMinutes()).padStart(2, '0')
    return `${mm}/${dd}/${yyyy} ${hh}:${min}`
  }

  function formatDateFilter(date) {
    const mm = String(date.getMonth() + 1).padStart(2, '0')
    const dd = String(date.getDate()).padStart(2, '0')
    const yyyy = String(date.getFullYear())
    return `${mm}/${dd}/${yyyy}`
  }

  const getProviderId = (provider) => provider?.value || provider?.ID || provider?.id || ''

  const normalizeDateOnly = (value) => {
    return toLeaveCalendarKey(value)
  }

  const getProviderLeaveRecords = useCallback(async (providerId, providerObj) => {
    const providerIdentities = new Set(getProviderIdentityValues({ ...providerObj, value: providerId }))

    if (!providerIdentities.size) return []

    const belongsToProvider = (leave) => {
      const leaveIdentities = getProviderIdentityValues({
        ID: leave?.EmployeeId || leave?.employeeId,
        Name: leave?.EmployeeName || leave?.employeeName,
        UserId: leave?.UserId || leave?.userId,
        Username: leave?.Username || leave?.UserName || leave?.username || leave?.userName,
      })

      return leaveIdentities.some((identity) => providerIdentities.has(identity))
    }

    if (providerLeaveCacheRef.current.has('ALL_LEAVES_FETCHED')) {
      const allLeaves = providerLeaveCacheRef.current.get('ALL_LEAVES_FETCHED');
      return allLeaves.filter(belongsToProvider)
    }

    try {
      const response = await SaveLeave({
        mode: 'SELECTALL',
        leaveId: '',
        employeeId: '',
        employeeName: '',
        employeeCountry: '',
        leaveType: '',
        leaveBalanceDays: '',
        fromDate: '',
        toDate: '',
        status: '',
        comments: '',
        remark: ''
      })

      const rawResponse = response?.d ?? response
      const parsedResponse = typeof rawResponse === 'string' ? JSON.parse(rawResponse) : rawResponse
      const leaveRecords = Array.isArray(parsedResponse?.data)
        ? parsedResponse.data
        : Array.isArray(parsedResponse?.Data)
          ? parsedResponse.Data
          : []

      providerLeaveCacheRef.current.set('ALL_LEAVES_FETCHED', leaveRecords)
      return leaveRecords.filter(belongsToProvider)
    } catch (error) {
      console.error('Failed to validate provider leave availability:', error)
      return []
    }
  }, [])

  const isProviderOnLeaveForDate = useCallback(
    async (provider, appointmentDate) => {
      const providerId = getProviderId(provider)
      const normalizedAppointmentDate = normalizeDateOnly(appointmentDate)

      if (!providerId || !normalizedAppointmentDate) return false

      const leaveRecords = await getProviderLeaveRecords(providerId, provider)

      return leaveRecords.some((leave) => {
        const leaveStatus = String(leave?.Status || leave?.status || '')
          .trim()
          .toLowerCase()

        if (!PROVIDER_LEAVE_BLOCKING_STATUSES.has(leaveStatus)) {
          return false
        }

        const leaveStartDate = normalizeDateOnly(leave?.FromDate || leave?.fromDate)
        const leaveEndDate = normalizeDateOnly(leave?.ToDate || leave?.toDate)

        if (!leaveStartDate || !leaveEndDate) {
          return false
        }

        return normalizedAppointmentDate >= leaveStartDate && normalizedAppointmentDate <= leaveEndDate
      })
    },
    [getProviderLeaveRecords],
  )

  const validateProviderLeaveAvailability = useCallback(
    async (entries = []) => {
      const checkedCombinations = new Set()

      for (const entry of entries) {
        const providerId = getProviderId(entry?.provider)
        const normalizedAppointmentDate = normalizeDateOnly(entry?.appointmentDate)

        if (!normalizedAppointmentDate) continue

        const cacheKey = `${providerId}_${normalizedAppointmentDate}`
        if (checkedCombinations.has(cacheKey)) continue

        checkedCombinations.add(cacheKey)

        // if (await isSchedulerHolidayDate(normalizedAppointmentDate, getSchedulerHolidayCountry(entry?.patient))) {
        //   toast.error(SCHEDULER_HOLIDAY_APPOINTMENT_ERROR, toastConfig)
        //   return false
        // }

        const providerLeaveRecords = providerId
          ? await getProviderLeaveRecords(providerId, entry?.provider)
          : []

        // if (!isWeekendAppointmentAllowed(normalizedAppointmentDate, entry?.provider, providerLeaveRecords)) {
        //   toast.error(WEEKEND_APPOINTMENT_MESSAGE, toastConfig)
        //   return false
        // }

        if (!providerId) continue

        const isProviderOnLeave = await isProviderOnLeaveForDate(entry?.provider, normalizedAppointmentDate)
        if (isProviderOnLeave) {
          toast.error(PROVIDER_ON_LEAVE_MESSAGE, toastConfig)
          return false
        }
      }

      return true
    },
    [getProviderLeaveRecords, isProviderOnLeaveForDate],
  )

  const getLatestData = async (getType, param1, param2) => {
    try {
      const result = await PatientAppointmentDetails(getType, '', param1, param2, '')
      const rawData = JSON.parse(result.d)?.Data || []
      let structuredData = rawData.flatMap((item) => {
        let parsedList = JSON.parse(item?.AppointmentDetails)
        return parsedList
      })

      let flatAppointments = structuredData.flatMap((p) => p?.appointmentDetails)

      return flatAppointments
    } catch (error) {
      console.log('Error in getAppointmentData', error)
    }
  }

  const hasCanceledAppointmentOnDate = useCallback((patientId, scheduleDate, providerId = '') => {
    const normalizedPatientId = String(patientId || '').trim()
    const normalizedScheduleDate = getAppointmentDateKey(scheduleDate)
    const normalizedProviderId = String(providerId || '').trim()

    if (!normalizedPatientId || !normalizedScheduleDate) return false

    return appointments.some((entry) => {
      const entryPatientId = String(entry?.current?.Patient?.value || entry?.data?.PatientID || '').trim()
      const entryStatus = String(entry?.data?.Status || '').trim().toLowerCase()
      const entryDate = getAppointmentDateKey(entry?.data?.StartTime)
      const entryProviderId = String(entry?.current?.Provider?.value || '').trim()

      return (
        entryPatientId === normalizedPatientId &&
        entryStatus === 'canceled' &&
        entryDate === normalizedScheduleDate &&
        (!normalizedProviderId || entryProviderId === normalizedProviderId)
      )
    })
  }, [appointments])

  const buildCanceledAppointmentLookup = useCallback((appointmentEntries = []) => (
    new Set(
      appointmentEntries
        .filter((item) => String(item?.data?.Status || '').trim().toLowerCase() === 'canceled')
        .map((item) => buildPatientProviderDateKey(
          item?.data?.PatientID || item?.current?.Patient?.value,
          item?.current?.Provider?.value,
          item?.data?.StartTime || item?.data?.ScheduleDate,
        )),
    )
  ), [])

  const getCanceledAppointmentLookupForDate = useCallback(async (scheduleDate) => {
    const normalizedScheduleDate = getAppointmentDateKey(scheduleDate)

    if (!normalizedScheduleDate) {
      return new Set()
    }

    const latestAppointments = await getAllAppointmentsDetails('GETBYSCHEDULEDATE', normalizedScheduleDate, '')
    return buildCanceledAppointmentLookup(latestAppointments || [])
  }, [buildCanceledAppointmentLookup])

  const getCanceledConflictAppointments = useCallback((candidateAppointments = [], existingAppointments = []) => {
    const canceledLookup = new Set(
      existingAppointments
        .filter((item) => String(item?.data?.Status || '').trim().toLowerCase() === 'canceled')
        .map((item) => buildPatientProviderDateKey(item?.data?.PatientID, item?.current?.Provider?.value, item?.data?.StartTime)),
    )

    return candidateAppointments.filter((item) => {
      const status = String(item?.data?.Status || '').trim().toLowerCase()
      if (status !== 'scheduled' && status !== 'rescheduled') return false

      const patientId = String(item?.data?.PatientID || '').trim()
      const scheduleDate = getAppointmentDateKey(item?.data?.StartTime)
      const appointmentId = String(item?.data?.AppointmentID || '').trim()
      const providerId = String(item?.current?.Provider?.value || '').trim()

      return existingAppointments.some((existingItem) => {
        const existingStatus = String(existingItem?.data?.Status || '').trim().toLowerCase()
        const existingPatientId = String(existingItem?.data?.PatientID || '').trim()
        const existingDate = getAppointmentDateKey(existingItem?.data?.StartTime)
        const existingAppointmentId = String(existingItem?.data?.AppointmentID || '').trim()
        const existingProviderId = String(existingItem?.current?.Provider?.value || '').trim()

        return (
          existingStatus === 'canceled' &&
          existingPatientId === patientId &&
          existingDate === scheduleDate &&
          existingProviderId === providerId &&
          existingAppointmentId !== appointmentId &&
          canceledLookup.has(buildPatientProviderDateKey(patientId, providerId, scheduleDate))
        )
      })
    })
  }, [])

  const handleManualScheduleData = async (notScheduleList) => {
    const providerAvailabilityEntries = notScheduleList.map((item) => ({
      provider: item?.selectedProvider,
      patient: item?.patient,
      appointmentDate: item?.selectedDate,
    }))

    const canScheduleAppointments = await validateProviderLeaveAvailability(providerAvailabilityEntries)
    if (!canScheduleAppointments) {
      return
    }

    let tempAppointments = []
    let alreadyScheduledPatients = []
    let canceledPatients = []
    let ineligiblePatients = []
    let checkForUpdateEntry = await getCheckForUpdateEntryForDate(selectedDate)

    await Promise.all(
      notScheduleList.map(async (item, index) => {
        const isFacility = item?.isFacility || false
        // const insurance  =  Manual[index]?.Insurance || null;
        // console.log("insuranse data ", insurance)

        // if(!insurance){
        //     alert(
        //         `Patient ${item.patient?.FirstName} ${item.patient?.LastName || ""} has no insurance.`
        //     );
        //     return;
        // }

        if (!isFacility) {
          alert(`Patient ${item.patient?.FirstName} ${item.patient?.LastName || ''} is not in the facility and cannot be scheduled.`)
          return
        }
        const priorAuth = item?.otherDetails?.priorAuth?.isRequired || false
        // if (!priorAuth) {
        //     alert(`Patient ${item.patient?.FirstName} ${item.patient?.LastName || ""}  has no prior authorization and cannot be scheduled.`);
        //     // args.cancel = true;
        //     return;

        // }
        if (priorAuth) {
          let noOfTimes = parseInt(item?.otherDetails?.priorAuth?.details?.noOfTimes, 10)

          if (isNaN(noOfTimes) || noOfTimes <= 0) {
            alert(`Patient ${item.patient?.FirstName} ${item.patient?.LastName || ''} has no prior insurance left and cannot be scheduled.`)
            // if (oldData?.data) {
            //     oldData.data.isInFacility = false;
            // }
            return
          }
        }

        let filtered = allAppointment?.filter((entry) => entry.patientID === item.patient.PatientID && entry.appointmentDetails.some((appt) => appt.data?.Status === 'Signed' || appt.data?.Status === 'Finish'))
        let oldData
        if (filtered.length > 0) {
          oldData = filtered[0].appointmentDetails.at(-1)
        }

        let time = item.selectedDate
        try {
          let props = {
            StartTime: formatDate(time),
            EndTime: formatDate(time),
            PatientID: item.patient.PatientID,
          }

          let date = handleScheduleAppointment(props, item.patient, oldData)

          if (await isSchedulerHolidayDate(date, getSchedulerHolidayCountry(item?.patient))) {
            toast.error(SCHEDULER_HOLIDAY_APPOINTMENT_ERROR, toastConfig)
            return
          }

          let organizeDate = formatDate(date)

          if (false && hasCanceledAppointmentOnDate(item.patient.PatientID, organizeDate.split(' ')[0], item?.selectedProvider?.value)) {
            canceledPatients.push(`${item.patient.FirstName} ${item.patient.LastName} ,`)
            return
          }

          let isAlredyScheduled = appointments.filter((entry) => entry.current.Patient.value === item.patient.PatientID && (entry.data?.Status === 'Scheduled' || entry.data?.Status === 'Rescheduled'))

          if (isAlredyScheduled.length > 0) {
            if (isAlredyScheduled[0].data.StartTime.split(' ')[0] === organizeDate.split(' ')[0]) {
              alreadyScheduledPatients.push(`${item.patient.FirstName} ${item.patient.LastName} ,`)
              return
            }

            const noteStatus = await GetPatientTreatmentsNotes(isAlredyScheduled[0])
            // if (noteStatus !== 'Finished') {
            //   ineligiblePatients.push(`${item.patient.FirstName} ${item.patient.LastName} ,`)
            //   return
            // }
          }
          let facility = locations?.find((fac) => fac.Name === item.patient.FacilityName)

          let localPatient = { value: item.patient.PatientID, label: `${item.patient.FirstName} ${item.patient.LastName}` }
          let localFacility = { value: facility?.ID || '', label: facility?.Name || '' }
          const localProvider = {
            value: item?.selectedProvider?.value,
            label: item?.selectedProvider?.label,
          }

          let Insurance = item?.Insurance || {}

          let data = {
            AppointmentName: [item.patient.FirstName, item.patient.MiddleName, item.patient.LastName].filter(Boolean).join(' '),
            StartTime: organizeDate,
            EndTime: organizeDate.replace(':00', ':15').replace(':30', ':45'),
            PatientID: item.patient.PatientID,
            StartTimezone: null,
            EndTimezone: null,
            Status: 'Scheduled',
            Notes: '',
            RoomNumber: item.patient.Room,
            VisitReason: filtered.length > 0 ? 'F/U (Follow Up)' : item?.patient?.Status == 'Follow Up' ? 'F/U (Follow Up)' : 'N/P (New Patient)',
            Insurance: Insurance,
            // isInFacility: oldData ? (oldData?.data?.isInFacility || false) : item?.isFacility,
            // otherDetails: oldData ? (oldData?.data?.otherDetails || {}) : (item?.otherDetails || {}),
            isInFacility: item?.isFacility || oldData?.data?.isInFacility || false,
            otherDetails: item?.otherDetails || oldData?.data?.otherDetails || {},
            Facility: facility?.Name || '',
            ProviderName: localProvider?.label || '',
            AppointmentID: Date.now() + index,
            ScheduleBy: 'manuel',
            // PatientStatus: filtered.length > 0 ? 'Follow Up' : item?.Status || 'NewPatient'
            Type: filtered.length > 0 ? 'Follow Up' : item?.patient?.Status == 'Follow Up' ? 'Follow Up' : 'New',
            CreatedDate: new Date(),
            patientSeen: true,
            notSeenReason: '',
            customReason: '',

            // isDragged: oldData ? (oldData?.data?.isDragged || false) : false,
          }

          let details = {
            data,
            current: {
              Patient: localPatient,
              Facility: localFacility,
              Provider: localProvider,
            },
          }


          if (Object.keys(details).length > 0) {
            if (item?.selectedProvider?.value) {
              if (checkForUpdateEntry?.JsonData && checkForUpdateEntry?.Id) {
                await appendPatientInTodaysList({
                  existingPatientData: checkForUpdateEntry.JsonData,
                  providerId: item?.selectedProvider?.value,
                  patientId: item.patient.PatientID,
                  id: checkForUpdateEntry.Id,
                  date: selectedDate,
                })
              } else {
                await addPatient({ providerId: item?.selectedProvider?.value, patientId: item.patient.PatientID, date: selectedDate })
                checkForUpdateEntry = await getCheckForUpdateEntryForDate(selectedDate, { force: true })
              }
            }

          }
          tempAppointments.push(details)
        } catch (error) {
          console.error('Error in scheduling------->', error)
        }
      }),
    )

    if (canceledPatients.length > 0) {
      toast.error(APPOINTMENT_ALREADY_CANCELLED_MESSAGE, toastConfig)
    }

    if (ineligiblePatients.length > 0 || alreadyScheduledPatients.length > 0) {
      let message = ''
      if (alreadyScheduledPatients.length > 0) {
        message += 'Appointment is already scheduled, please select another date.\n\n'
      }
      if (ineligiblePatients.length > 0) {
        message += `${ineligiblePatients.join(' ')} current note is not finished. Please finish it and try again.`
      }
      toast.error(message.trim(), toastConfig)
    }

    if (tempAppointments.length > 0) {
      // const latestData = await getLatestData('GETBYSCHEDULEDATE', filterDate?.split[' '][0])
      const latestData = await getAllAppointmentsDetails('GETBYSCHEDULEDATEANDPROVIDERID', formatDateFilter(selectedDate), selectedProvider?.value)
      const canceledLookupForDate = await getCanceledAppointmentLookupForDate(selectedDate)
      const type = latestData.length > 0 ? 'UPDATE' : 'INSERT'
      const canceledDuplicates = tempAppointments.filter((newData) =>
        false && canceledLookupForDate.has(buildPatientProviderDateKey(newData?.data?.PatientID, newData?.current?.Provider?.value, newData?.data?.StartTime)),
      )
      const duplicates = tempAppointments.filter((newData) =>
        latestData.some(
          (item) => item.data.PatientID === newData.data.PatientID && item.data.Status === 'Scheduled' && getAppointmentDateKey(item.data.StartTime) === getAppointmentDateKey(newData.data.StartTime), // same date
        ),
      )

      const blockedPatientIds = new Set([
        ...duplicates.map((dup) => dup.data.PatientID),
        ...canceledDuplicates.map((dup) => dup.data.PatientID),
      ])
      const validAppointments = tempAppointments.filter((newData) => !blockedPatientIds.has(newData.data.PatientID))

      if (canceledDuplicates.length > 0) {
        toast.error(APPOINTMENT_ALREADY_CANCELLED_MESSAGE, toastConfig)
      }

      if (duplicates.length > 0) {
        toast.error('Appointment is already scheduled, please select another date.', toastConfig)
      }

      if (validAppointments.length > 0) {
        const appointmentsWithReusedNotes = reuseCanceledAppointmentNoteIds(validAppointments, appointments)
        const combinedAppointments = [...latestData, ...appointmentsWithReusedNotes]
        await handleSubmit(combinedAppointments, type, 'create')
      }
    }

    setSelectedPatients([])
  }

  useEffect(() => {
    if (!selectedDate) return
    setSelectedProvider(null)
    setSearchedFacilityData({})
    setSelectedPatients([])
    setToggleStatePatients(false)
    setToggleStateFacility(false)
  }, [selectedDate])

  useEffect(() => {
    let isMounted = true

    const bootstrapScheduler = async () => {
      if (location.pathname !== '/home/appointmentScheduler') return
      setIsProviderLoading(true)
      try {
        invalidateSchedulerPatientCache()
        await Promise.all([getFacilities(), getProviders(), getData(toggleStatePatients, selectedDate, { forcePatients: true })])
        if (isMounted) {
          initialLoadCompleteRef.current = true
        }
      } finally {
        if (isMounted) {
          setIsProviderLoading(false)
        }
      }
    }

    bootstrapScheduler()

    return () => {
      isMounted = false
    }
  }, [getData, getFacilities, getProviders, invalidateSchedulerPatientCache, location.key, location.pathname])

  useEffect(() => {
    if (!initialLoadCompleteRef.current) return
    getData(toggleStatePatients, selectedDate)
  }, [getData, selectedDate, toggleStatePatients])

  const isProviderScheduledAtFacility = (facility, provider) => {
    const facilityId = facility?.ID ?? facility?.id ?? facility?.FacilityID ?? facility?.facilityId ?? facility?.value ?? facility
    const facilityName = String(facility?.Name ?? facility?.name ?? facility?.FacilityName ?? facility?.facilityName ?? facility?.label ?? '').trim().toLowerCase()

    return (provider?.Facilities || []).some((providerFacility) => {
      const providerFacilityId = providerFacility?.FacilityID ?? providerFacility?.facilityID ?? providerFacility?.FacilityId ?? providerFacility?.facilityId ?? providerFacility?.ID ?? providerFacility?.id ?? providerFacility?.value
      const providerFacilityName = String(providerFacility?.FacilityName ?? providerFacility?.facilityName ?? providerFacility?.Name ?? providerFacility?.name ?? providerFacility?.label ?? '').trim().toLowerCase()

      const facilityMatches =
        (facilityId !== undefined && facilityId !== null && String(providerFacilityId) === String(facilityId)) ||
        (facilityName && providerFacilityName === facilityName)

      return facilityMatches
    })
  }

  const handleDeleteAppointment = async (updateData, providerID, selectDate) => {
    try {
      setIsPageLoading(true)

      const groupedData = []

      updateData.forEach((entry) => {
        const patientID = entry.current.Patient.value

        const existing = groupedData.find((item) => item.patientID === patientID)

        if (existing) {
          existing.appointmentDetails.push({ data: entry.data, current: entry.current })
        } else {
          groupedData.push({
            patientID,
            appointmentDetails: [{ data: entry.data, current: entry.current }],
          })
        }
      })

      // await SaveExtra('Update', 'multiAppointmentsData', JSON.stringify(groupedData), allAppointment[0]?.Id || 388)
      const ID = await getAppointmentDataID('GETBYSCHEDULEDATEANDPROVIDERID', selectDate, providerID)
      await PatientAppointmentDetails('UPDATE', ID, selectDate, providerID, JSON.stringify(groupedData))
      if (selectedDate) {
        getData(toggleStatePatients, selectDate)
      } else {
        getData(toggleStatePatients, '', '', 'GET')
      }
      toast.success('Appointment Deleted Successfully', toastConfig)
    } catch (error) {
      toast.error('Failed to Delete', toastConfig)
      console.error('Failed to save ', error)
    } finally {
      setIsPageLoading(false)
    }
  }

  const handleSubmit = async (updateData, apiCallType = 'INSERT', type = null) => {
    try {
      setIsPageLoading(true)

      const providerAvailabilityEntries = updateData.map((entry) => ({
        provider: entry?.current?.Provider,
        appointmentDate: entry?.data?.StartTime || selectedDate,
      }))

      const canScheduleAppointments = await validateProviderLeaveAvailability(providerAvailabilityEntries)
      if (!canScheduleAppointments) {
        return
      }

      const groupedData = []

      updateData.forEach((entry) => {
        const patientID = entry.current.Patient.value

        const existing = groupedData.find((item) => item.patientID === patientID)

        if (existing) {
          existing.appointmentDetails.push({ data: entry.data, current: entry.current })
        } else {
          groupedData.push({
            patientID,
            appointmentDetails: [{ data: entry.data, current: entry.current }],
          })
        }
      })

      const scheduledDate = formatDateFilter(selectedDate)
      const providerID = selectedProvider?.value
      // await SaveExtra('Update', 'multiAppointmentsData', JSON.stringify(groupedData), allAppointment[0]?.Id || 388)

      if (scheduledDate && providerID) {
        const latestDateAppointments = await getAllAppointmentsDetails('GETBYSCHEDULEDATE', scheduledDate, '')
        const canceledConflicts = getCanceledConflictAppointments(updateData, latestDateAppointments || [])

        if (false && canceledConflicts.length > 0) {
          toast.error(APPOINTMENT_ALREADY_CANCELLED_MESSAGE, toastConfig)
          return
        }

        if (apiCallType == 'UPDATE') {
          const ID = await getAppointmentDataID('GETBYSCHEDULEDATEANDPROVIDERID', formatDateFilter(selectedDate), selectedProvider?.value)
          await PatientAppointmentDetails('UPDATE', ID, scheduledDate, providerID, JSON.stringify(groupedData))
        } else if (apiCallType == 'INSERT') {
          await PatientAppointmentDetails('INSERT', '', scheduledDate, providerID, JSON.stringify(groupedData))
        }
      } else {
        console.error('an error occured while submiting data')
      }

      // getAppointmentData();
      getData(toggleStatePatients, scheduledDate)
      if (type === 'save') {
        // toast.success('Saved Successfully', toastConfig);
      } else if (type === 'create') {
        toast.success('Created Successfully', toastConfig)
      }
    } catch (error) {
      toast.error('Failed to save', toastConfig)
      console.error('Failed to save ', error)
    } finally {
      setIsPageLoading(false)
    }
  }

  const handleAppointmentInsuranceAuth = async (updateData, date, id) => {
    try {
      setIsPageLoading(true)

      const groupedData = []

      updateData.forEach((entry) => {
        const patientID = entry?.current?.Patient.value

        const existing = groupedData.find((item) => item.patientID === patientID)

        if (existing) {
          existing.appointmentDetails.push({ data: entry.data, current: entry.current })
        } else {
          groupedData.push({
            patientID,
            appointmentDetails: [{ data: entry.data, current: entry.current }],
          })
        }
      })
      if (id && date) {
        const ID = await getAppointmentDataID('GETBYSCHEDULEDATEANDPROVIDERID', date, id)
        await PatientAppointmentDetails('UPDATE', ID, date, id, JSON.stringify(groupedData))
      }

      // getAppointmentData();
      if (selectedDate) {
        getData(toggleStatePatients, selectedDate)
      } else {
        getData(toggleStatePatients, '', '', 'GET')
      }

      // toast.success('Saved data Successfully', toastConfig)
    } catch (error) {
      toast.error('Failed to save', toastConfig)
      console.error('Failed to save ', error)
    } finally {
      setIsPageLoading(false)
    }
  }

  const handleReorderSubmit = async (updateData, type = null) => {
    try {
      setIsPageLoading(true)

      const groupedData = []
      updateData.forEach((entry) => {
        const patientID = entry.current.Patient.value
        const existing = groupedData.find((item) => item.patientID === patientID)
        if (existing) {
          existing.appointmentDetails.push({ data: entry.data, current: entry.current })
        } else {
          groupedData.push({
            patientID,
            appointmentDetails: [{ data: entry.data, current: entry.current }],
          })
        }
      })

      const latestData = await getAllAppointments('GETBYSCHEDULEDATE', formatDateFilter(selectedDate))
      const orderedOriginalStructureData = groupedData
        .map((group) => {
          return latestData.find((item) => item.patientID === group.patientID)
        })
        .filter(Boolean)

      const ID = await getAppointmentDataID('GETBYSCHEDULEDATEANDPROVIDERID', formatDateFilter(selectedDate), selectedProvider?.value)

      if (ID) {
        await PatientAppointmentDetails('UPDATE', ID, formatDateFilter(selectedDate), selectedProvider?.value, JSON.stringify(orderedOriginalStructureData))
      }
      // await SaveExtra('Update', 'multiAppointmentsData', JSON.stringify(orderedOriginalStructureData), allAppointment[0]?.Id || 388)

      getData(toggleStatePatients, formatDateFilter(selectedDate))
    } catch (error) {
      toast.error('Failed to reorder', toastConfig)
    } finally {
      setIsPageLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      if (!accordionState?.length) return

      const updatesMap = new Map()

      for (const item of accordionState) {
        if (!item?.AppointmentID) continue

        const oldSchedulingData = appointments.find(
          (a) => a.data.AppointmentID === item.AppointmentID
        )

        if (!oldSchedulingData) continue

        const current = {
          ...oldSchedulingData.current,
          Insurance: item.Insurance ?? oldSchedulingData.current?.Insurance,
          otherDetails: item.otherDetails ?? oldSchedulingData.current?.otherDetails,
        }

        const data = {
          ...oldSchedulingData.data,
          Insurance: current.Insurance,
          otherDetails: current.otherDetails,
        }

        const appointmentPatient = patientData.find((patient) => String(patient?.PatientID) === String(data?.PatientID)) || current?.Patient || {}
        if (await isSchedulerHolidayDate(data.StartTime, getSchedulerHolidayCountry(appointmentPatient))) {
          toast.error(SCHEDULER_HOLIDAY_APPOINTMENT_ERROR, toastConfig)
          return
        }

        const date = data.StartTime.split(' ')[0]
        const providerId = current?.Provider?.value
        const key = `${date}_${providerId}`

        if (!updatesMap.has(key)) {
          const latestData = await getAllAppointmentsDetails(
            'GETBYSCHEDULEDATEANDPROVIDERID',
            date,
            providerId
          )

          updatesMap.set(key, latestData)
        }

        const existing = updatesMap.get(key)

        const updatedAppointments = existing.some(
          (a) => a.data.AppointmentID === item.AppointmentID
        )
          ? existing.map((a) =>
            a.data.AppointmentID === item.AppointmentID
              ? { data, current }
              : a
          )
          : existing

        updatesMap.set(key, updatedAppointments)
      }


      await Promise.all(
        [...updatesMap.entries()].map(([key, updatedAppointments]) => {
          const [date, providerId] = key.split('_')
          return handleAppointmentInsuranceAuth(updatedAppointments, date, providerId)
        })
      )

      toast.success('Saved data successfully', toastConfig)
    } catch (error) {
      console.error('Error while saving appointments data', error)
      toast.error('Failed to save data', toastConfig)
    }
  }
  // const handleSave = () => {
  //   try {
  //     console.log('accordionState',accordionState)
  //     let editData = accordionState?.map(async (item, index) => {
  //        console.log('item',item,appointments)

  //       if (!item || !item.AppointmentID) return true
  //       let oldSchedulingData = appointments.filter((appointmentItem) => appointmentItem.data.AppointmentID === item.AppointmentID)
  //         console.log('oldSchedulingData',oldSchedulingData)
  //       if (!oldSchedulingData.length) return true

  //       let current = {
  //         ...oldSchedulingData[0]?.current,
  //         Insurance: item.Insurance || oldSchedulingData[0]?.current?.Insurance,
  //         otherDetails: item.otherDetails || oldSchedulingData[0]?.current?.otherDetails,
  //       }

  //       let data = {
  //         ...oldSchedulingData[0]?.data,
  //         Insurance: current.Insurance,
  //         otherDetails: current.otherDetails,
  //       }

  //       let FinalData = { data, current }

  //       const latestData = await getAllAppointmentsDetails('GETBYSCHEDULEDATEANDPROVIDERID', data.StartTime.split(' ')[0], current?.Provider?.value)
  //       console.log('latestData', latestData, FinalData, accordionState)

  //       if (latestData.length > 0) {

  //                   const exists = latestData.some(
  //                   (item) => item.data.AppointmentID === FinalData.data.AppointmentID 
  //                   );

  //                   // Update array properly
  //                   const updatedAppointments = exists
  //                   ? latestData.map((item) =>
  //                   item.data.AppointmentID  === FinalData.data.AppointmentID 
  //                   ? { data, current }
  //                   : item
  //                   )
  //                   : latestData;
  //          // }
  //         // const updatedAppointments=latestData.map((appointment) =>

  //         //   console.log('appointment.data.AppointmentID',appointment.data.AppointmentID)

  //         //   (appointment.data.AppointmentID == item.AppointmentID ? { data, current } : appointment))


  //         // const updatedAppointments = latestData.some((appointment) => appointment.data.AppointmentID == item.AppointmentID) ? 
  //         // latestData.map((appointment) => (appointment.data.AppointmentID == item.AppointmentID ? { data, current } : appointment))
  //         //  : latestData

  //         console.log('updatedAppointments', updatedAppointments)
  //         handleAppointmentInsuranceAuth(updatedAppointments, data.StartTime.split(' ')[0], current?.Provider?.value)
  //         return true
  //       } else {
  //         return false
  //       }
  //     })

  //     if (editData) {
  //       toast.success('Saved data Successfully', toastConfig)
  //     }
  //   } catch (error) {
  //     console.error('Error while saving appointments data', error)
  //   }
  // }
  const handleSelectAll = (type, checked) => {
    const facilitiesOfType = searchedFacilityData[type]

    if (!facilitiesOfType) return

    const patientsUnderType = facilitiesOfType.flatMap((facility) =>
      notSchedule
        .filter((p) => {
          if (p.FacilityName !== facility.Name || !isProviderScheduledAtFacility(facility, selectedProvider, selectedDate)) {
            return false
          }

          if (toggleStatePatients) {
            return true
          }

          const patientId = String(p?.PatientID || '').trim()
          return !scheduledPatientIdsForSelectedDate.has(patientId)
        })
        .map((patient) => ({
          patient,
          selectedProvider,
          selectedDate,
          isFacility: true,
        })),
    )

    if (checked) {
      // Add patients - fetch insurance for all selected patients
      const newPatients = patientsUnderType.filter((newPatient) => !selectedPatients.some((sel) => sel.patient.PatientID === newPatient.patient.PatientID))
      
      Promise.all(
        newPatients.map(async (p) => {
          try {
            let patientInsurance = await PatientInsuranceFn('SELECT', '', p.patient.PatientID)
            let insuranceData = []
            if (patientInsurance?.d) {
              insuranceData = JSON.parse(patientInsurance.d)?.Data || []
            }
            return {
              ...p,
              Insurance: insuranceData && insuranceData.length > 0 ? insuranceData[0] : {},
            }
          } catch (error) {
            console.error('Error fetching insurance data for patient:', p.patient.PatientID, error)
            return {
              ...p,
              Insurance: {},
            }
          }
        })
      ).then((patientsWithInsurance) => {
        setSelectedPatients((prev) => [...prev, ...patientsWithInsurance])
      })
    } else {
      // Remove patients
      const patientIDsToRemove = new Set(patientsUnderType.map((p) => p.patient.PatientID))
      setSelectedPatients((prev) => prev.filter((sel) => !patientIDsToRemove.has(sel.patient.PatientID)))
    }
  }

  const handleFacilitySelectAll = (facility, patients, checked) => {
    if (checked) {
      const newSelections = patients
        .filter((p) => !selectedPatients.some((sel) => sel.patient.PatientID === p.PatientID))
      
      Promise.all(
        newSelections.map(async (p) => {
          try {
            let patientInsurance = await PatientInsuranceFn('SELECT', '', p.PatientID)
            let insuranceData = []
            if (patientInsurance?.d) {
              insuranceData = JSON.parse(patientInsurance.d)?.Data || []
            }
            return {
              patient: p,
              selectedProvider,
              selectedDate,
              isFacility: true,
              Insurance: insuranceData && insuranceData.length > 0 ? insuranceData[0] : {},
            }
          } catch (error) {
            console.error('Error fetching insurance data for patient:', p.PatientID, error)
            return {
              patient: p,
              selectedProvider,
              selectedDate,
              isFacility: true,
              Insurance: {},
            }
          }
        })
      ).then((patientsWithInsurance) => {
        setSelectedPatients((prev) => [...prev, ...patientsWithInsurance])
      })
    } else {
      const idsToRemove = new Set(patients.map((p) => p.PatientID))
      setSelectedPatients((prev) => prev.filter((sel) => !idsToRemove.has(sel.patient.PatientID)))
    }
  }

  const formatDateString = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
  }

  const scheduledForSelectedDateAndProvider = onlyScheduled?.filter((item) => {
    const apptDate = formatDateString(item.data?.StartTime)
    const selected = formatDateString(selectedDate)
    const providerMatch = item.data?.ProviderName?.trim() === selectedProvider?.Name?.trim()
    return apptDate === selected && providerMatch
  })

  const scheduledPatientIdsForSelectedDate = useMemo(() => {
    const selectedDateKey = getAppointmentDateKey(selectedDate)

    if (!selectedDateKey) {
      return new Set()
    }

    return new Set(
      onlyScheduled
        .filter((item) => getAppointmentDateKey(item?.data?.StartTime) === selectedDateKey)
        .map((item) => String(item?.data?.PatientID || item?.current?.Patient?.value || '').trim())
        .filter(Boolean),
    )
  }, [onlyScheduled, selectedDate])

  const handleAvialableProvider = (facility) => {
    let availableProvidersList = availableProviders.filter((provider) => isProviderScheduledAtFacility(facility, provider, selectedDate))

    // setAvailableProviderForSelFacility(availableProvidersList);
    //  setShowAvailableProvider(true);
    return availableProvidersList
      .filter((item) => item.ID !== selectedProvider?.value)
      .map((item) => item.Name)
      .join(', ')
  }

  const notInFacility = () => {
    const scheduledFacilityIds = scheduledForSelectedDateAndProvider.map((item) => parseInt(item.current.Facility.value))

    const unscheduledFacilities = selectedProvider?.Facilities?.filter((facility) => !scheduledFacilityIds.includes(parseInt(facility.FacilityID)))
    const updatedProvider = {
      ...selectedProvider,
      Facilities: unscheduledFacilities,
    }

    setNotScheduledFacility(updatedProvider)
  }
  const handleMap = () => {
    if (!selectedProvider) {
      toast.error('Please select a provider.', toastConfig)
      return
    }

    const scheduledFacilityIds = scheduledForSelectedDateAndProvider.map((item) => parseInt(item.current.Facility.value))
    const unscheduledFacilities = selectedProvider?.Facilities?.filter((facility) => !scheduledFacilityIds.includes(parseInt(facility.FacilityID)))
    const notScheduledProvider = {
      ...selectedProvider,
      Facilities: unscheduledFacilities,
    }

    setNotScheduledFacility(notScheduledProvider)

    const mapState = {
      scheduledPatient: scheduledForSelectedDateAndProvider,
      selectedProviderData: notScheduledProvider,
      selectedProvider,
    }

    history.push(
      `/home/schedulermap?provider=${encodeURIComponent(selectedProvider?.Name || '')}`,
      mapState,
    )
  }

  const GetPatientTreatmentsNotes = async (appointment) => {
    if (!appointment) return

    try {
      const patientId = appointment.current.Patient.value
      const noteId = appointment?.data?.NoteID
      const dataResponse = await PatientNoteDataCRUD('GET', patientId)
      const parsedData = JSON.parse(dataResponse?.d)?.Data

      const noteStats = parsedData.filter((data) => data.id == noteId)

      // setFilteredPatientData(updatedPatients)
      if (noteStats.length > 0) {
        return noteStats[0].status || null
      } else {
        null
      }
    } catch (error) {
      console.error('Error fetching patient orders', error)
      toast.error('Error in patient treatment', toastConfig)
    }
  }

  const [searchTerms, setSearchTerms] = useState({})


  const handleSearchChange = (facilityId, value) => {
    setSearchTerms((prev) => ({
      ...prev,
      [facilityId]: value,
    }))
  }

  const [creatingPatientLoader, setCreatingPatientLoader] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [patinetWithFacilityData, setPatinetWithFacilityData] = useState(null)
  const [patientInsurance, setPatientInsurance] = useState("")
  const [patientID, setPatientID] = useState(null)
  const [editPatientFacility, setEditPatientFacility] = useState({
    facility: '',
    roomNumber: '',
  })

  const [childformData, setChildFormData] = useState({
    FirstName: '',
    MiddleName: '',
    LastName: '',
    DOB: '',
    Gender: '',
    Age: '0',
    PhoneNumber: '',
    EmergencyName: '',
    EmergencyPhone: '',
    Address: '',
    State: '',
    City: '',
    ZipCode: '',
    Country: '',
    insurance: '',
    Status: 'NewPatient',
  })



  const resetStates = () => {
    setChildFormData({
      FirstName: '',
      MiddleName: '',
      LastName: '',
      DOB: '',
      Gender: '',
      Age: '0',
      PhoneNumber: '',
      EmergencyName: '',
      EmergencyPhone: '',
      Address: '',
      State: '',
      City: '',
      ZipCode: '',
      Country: '',
      insurance: '',
      Status: 'NewPatient',
    })

    setEditPatientFacility({
      facility: '',
      roomNumber: '',
    })
    setModalVisible(false)
  }

  const handleAddPatientData = async (facility, rowSelected) => {
    setEditPatientFacility((prev) => ({
      ...prev,
      roomNumber: rowSelected?.Room,
      facility: facility?.Name,
    }))
    setPatinetWithFacilityData(facility)

    let patientInsurance = await PatientInsuranceFn('SELECT', '', rowSelected.PatientID)

    try {
      patientInsurance = JSON.parse(patientInsurance.d)?.Data
      setPatientInsurance(patientInsurance)
    } catch (e) {
      console.error('error', e)
    }

    setPatientID(rowSelected.PatientID)
    setChildFormData({
      FirstName: rowSelected.FirstName || '',
      MiddleName: rowSelected.MiddleName || '',
      LastName: rowSelected.LastName || '',
      DOB: rowSelected.DOB || '',
      Gender: rowSelected.Gender || '',
      Age: rowSelected.Age || '0',
      PhoneNumber: rowSelected.PhoneNumber || '',
      EmergencyName: rowSelected.EmergencyName || '',
      EmergencyPhone: rowSelected.EmergencyPhone || '',
      Address: rowSelected.Address || '',
      State: rowSelected.State || '',
      City: rowSelected.City || '',
      ZipCode: rowSelected.ZipCode || '',
      Country: rowSelected.Country || '',
      Status: rowSelected.Status || '',
      Room: rowSelected.Room || '',
    })

    setModalVisible(true)
  }



  const handlePatientSubmit = async () => {
    setCreatingPatientLoader(true)
    try {
      let a = {
        FacilityID: patinetWithFacilityData?.ID || '',
        FacilityName: patinetWithFacilityData?.Name || '',
        Room: editPatientFacility?.roomNumber || -1,
      }
      let savePatientInfo

      if (patientID !== '') {
        const today = new Date()
        const mm = String(today.getMonth() + 1).padStart(2, '0')
        const dd = String(today.getDate()).padStart(2, '0')
        const yyyy = today.getFullYear()
        const formattedDate = `${mm}/${dd}/${yyyy}`
        savePatientInfo = SavePatients('Update', JSON.stringify(childformData), JSON.stringify(a), '', patientID, "")
        toast.success('Patient Updated Successfully', toastConfig)
      }
      resetStates()
      setModalVisible(false)
      setCreatingPatientLoader(false)
      invalidateSchedulerPatientCache()
      await getData(toggleStatePatients, selectedDate, { forcePatients: true })
    } catch (error) {
      setCreatingPatientLoader(false)
      console.error("error while patient updating", error)
      toast.error('Failed to save patient data. Please try again.', toastConfig)
    }
  }

  return (
    <div className="appointment-scheduler-page dashboard-dark-theme scheduler-theme" style={{ height: '92vh', display: 'flex', flexDirection: 'column' }}>
      <div className="mb-3 p-3 border rounded scheduler-theme__toolbar">
        <div className="d-flex justify-content-between flex-wrap gap-3">
          <div className="d-flex flex-wrap align-items-end gap-3">
            <div className="d-flex flex-column scheduler-theme__field scheduler-theme__field--wide">
              <label className="form-label fw-semibold mb-1">Appointment Date</label>
              <div className="scheduler-theme__input-shell scheduler-theme__datepicker-shell">
                <DatePickerComponent value={selectedDate} onChange={handleDateChange} placeholder="Select date" format="MM/dd/yyyy" cssClass="e-input" />
              </div>
            </div>

            <div className="d-flex flex-column scheduler-theme__field scheduler-theme__field--wide">
              <label className="form-label fw-semibold mb-1">Provider</label>
              <div className="scheduler-theme__input-shell scheduler-theme__select-shell">
                <AppSelect
                  options={availableProviders.map((item) => ({
                    label: item.Name,
                    value: item.ID,
                    ...item,
                  }))}
                  isClearable
                  isLoading={isProviderLoading}
                  value={selectedProvider}
                  onChange={setSelectedProvider}
                  placeholder="Select Provider"
                />
              </div>
            </div>

            <div className="d-flex gap-2">
              <CButton className="ui-action-btn ui-action-btn--primary scheduler-theme__toolbar-button" onClick={handleSearch}>
                Search
              </CButton>
              <CButton className="ui-action-btn ui-action-btn--secondary scheduler-theme__toolbar-button" onClick={handleMap}>
                Map
              </CButton>
            </div>
          </div>
        </div>
      </div>

      <div className="appointment-scheduler-panels px-2 scheduler-theme__panels" style={{ flex: 1, height: '60vh', overflow: 'hidden', minHeight: 0 }}>
        <div
          className="appointment-scheduler-panel d-flex flex-column px-2 border scheduler-theme__panel"
          style={{
            flex: '1 1 0',
            minWidth: 0,
            padding: '10px',
            display: 'flex',
            flexDirection: 'column',
            height: '100%', // ensure it stretches to available space
          }}
        >
          <div className="flex-grow-1 ">
            <div className="appointment-scheduler-panel-header d-flex align-items-center justify-content-between rounded px-4 py-2 border scheduler-theme__panel-header">
              <div className="d-flex align-items-center gap-2">
                <span className="fw-semibold scheduler-theme__panel-title">Selected Patients: {selectedPatients?.length ?? 0}</span>
              </div>

              <div className="appointment-scheduler-stats d-flex align-items-center gap-2">
                <div className="d-inline-flex align-items-center gap-2 flex-nowrap">
                  <label htmlFor="facilityToggle" className="mb-0 scheduler-theme__label-muted fw-medium">
                    Show All Facility
                  </label>
                  <CFormSwitch id="facilityToggle" className="mt-2 ui-switch" checked={toggleStateFacility} onChange={() => setToggleStateFacility((prev) => !prev)} />
                </div>

                <div className="scheduler-theme__divider"></div>

                <div className="d-flex align-items-center gap-2">
                  <span className="fw-medium scheduler-theme__label-muted">Show All Patients</span>
                  <CFormSwitch id="patientsToggle" className="mt-2 ui-switch" checked={toggleStatePatients} onChange={() => setToggleStatePatients((prev) => !prev)} />
                </div>
              </div>
            </div>
            <div style={{ maxHeight: '57.5vh' }} className="mt-2 scheduler-theme__scroll">
              {Object.entries(searchedFacilityData).map(([type, facilities], index) => {
                let facilitiesWithPatients = []
                if (selectedProvider === null || selectedProvider === undefined) {
                  facilitiesWithPatients = [...facilities].sort((a, b) => a.Name.localeCompare(b.Name))
                } else {
                  facilitiesWithPatients = facilities
                    .filter((facility) => {
                      if (!isProviderScheduledAtFacility(facility, selectedProvider)) {
                        return false
                      }

                      if (toggleStateFacility) {
                        return true
                      }

                      return notSchedule.some((patient) => patient.FacilityName === facility.Name)
                    })
                    .sort((a, b) => a.Name.localeCompare(b.Name))
                }

                return (
                  <div key={type} className="px-2 ">
                    <SchedulerAccordian
                      title={type}
                      handleSelectAll={(checked) => handleSelectAll(type, checked)}
                      content={
                        <div>
                          {facilitiesWithPatients.length === 0 ? (
                            <div className="text-center text-muted scheduler-theme__empty">
                              No patients available under {type} facilities.
                            </div>
                          ) : (
                            facilitiesWithPatients.map((facility) => {
                              const searchTerm = searchTerms[facility.ID] || ''
                              const patientsForFacility = notSchedule.filter((p) => {
                                if (p.FacilityName !== facility.Name) return false

                                if (toggleStatePatients) return true

                                const patientId = String(p?.PatientID || '').trim()
                                return !scheduledPatientIdsForSelectedDate.has(patientId)
                              })

                              //  if (patientsForFacility.length === 0) return null;
                              const availableProviderData = handleAvialableProvider(facility)
                              return (
                                <div key={facility.ID} className="scheduler-theme__facility-block">
                                  <SchedulerAccordian
                                    type={'facilityType'}
                                    title={
                                      <div>
                                        {facility.Name}
                                        {availableProviderData && <span className="scheduler-theme__facility-meta">({availableProviderData})</span>}{' '}
                                      </div>
                                    }
                                    handleFacilitySelectAll={(checked) => handleFacilitySelectAll(facility, patientsForFacility, checked)}
                                    // isFacilityFullySelected={
                                    //     patientsForFacility.every(p => selectedPatients.some(sel => sel.patient.PatientID === p.PatientID))
                                    // }
                                    handleAvialableProvider={() => handleAvialableProvider(facility)}
                                    content={
                                      <div>
                                        {patientsForFacility.length > 0 ? (
                                          (() => {
                                            const scheduled = []
                                            const unscheduled = []

                                            const filteredPatients = patientsForFacility.filter((patient) => {
                                              if (!searchTerm || !searchTerm.trim()) return true
                                              const fullName = [patient.FirstName, patient.MiddleName, patient.LastName].filter(Boolean).join(' ')
                                              return nameMatchesSearch(fullName, searchTerm)
                                            })
                                            filteredPatients.forEach((patient) => {
                                              const isSelected = selectedPatients.some((sel) => sel.patient.PatientID === patient.PatientID)

                                              let appointmentData = {}
                                              let appointmentDetails = []

                                              if (toggleStatePatients) {
                                                appointmentDetails = appointments.filter((item) => item.current.Patient.value === patient.PatientID && (item.data.Status === 'Scheduled' || item.data.Status === 'Rescheduled'))

                                                if (appointmentDetails.length > 0) {
                                                  appointmentData = appointmentDetails[0]
                                                }
                                              }

                                              const render = (
                                                <CCallout
                                                  key={patient.PatientID}
                                                  className={`scheduler-theme__patient-callout ${patient.Status === 'NewPatient' ? 'scheduler-theme__patient-callout--new' : 'scheduler-theme__patient-callout--followup'}`}
                                                >
                                                  <div className="scheduler-theme__patient-main">
                                                    <input
                                                      type="checkbox"
                                                      className="form-check-input scheduler-theme__patient-checkbox"
                                                      id={`patient-${patient.PatientID}`}
                                                      value={patient.PatientID}
                                                      checked={isSelected}
                                                      onChange={async (e) => {
                                                        if (e.target.checked) {
                                                          try {
                                                            let patientInsurance = await PatientInsuranceFn('SELECT', '', patient.PatientID)
                                                            let insuranceData = []
                                                            if (patientInsurance?.d) {
                                                              insuranceData = JSON.parse(patientInsurance.d)?.Data || []
                                                            }
                                                            
                                                            setSelectedPatients((prev) => [
                                                              ...prev,
                                                              {
                                                                patient,
                                                                selectedProvider,
                                                                selectedDate,
                                                                isFacility: true,
                                                                Insurance: insuranceData && insuranceData.length > 0 ? insuranceData[0] : {},
                                                              },
                                                            ])
                                                          } catch (error) {
                                                            console.error('Error fetching insurance data:', error)
                                                            setSelectedPatients((prev) => [
                                                              ...prev,
                                                              {
                                                                patient,
                                                                selectedProvider,
                                                                selectedDate,
                                                                isFacility: true,
                                                                Insurance: {},
                                                              },
                                                            ])
                                                          }
                                                        } else {
                                                          setSelectedPatients((prev) => prev.filter((sel) => sel.patient.PatientID !== patient.PatientID))
                                                        }
                                                      }}
                                                    />
                                                    <div className="scheduler-theme__patient-copy">
                                                      <div className="d-flex align-items-center">
                                                        <div
                                                          className="flex-shrink-0 scheduler-theme__patient-name"
                                                          style={{
                                                            textOverflow: 'ellipsis',
                                                            width: '150px',
                                                            overflow: 'hidden',
                                                            whiteSpace: 'nowrap',
                                                          }}
                                                        >
                                                          {patient.FirstName} {patient.MiddleName} {patient.LastName}
                                                        </div>

                                                        <div className="d-flex align-items-center flex-shrink-0 me-1">
                                                          {patient.Room && (
                                                            <div className="room-spill scheduler-theme__pill scheduler-theme__pill--room" title={`Room : ${patient.Room}`}>
                                                              {patient.Room}
                                                            </div>
                                                          )}

                                                          {appointmentDetails?.length > 0 && <div className="schedule1-spill scheduler-theme__pill scheduler-theme__pill--provider">{appointmentData?.data?.ProviderName}</div>}

                                                          {appointmentDetails?.length > 0 && <div className="schedule-spill scheduler-theme__pill scheduler-theme__pill--date">{appointmentData?.data?.StartTime?.split(' ')?.[0]}</div>}
                                                        </div>
                                                      </div>
                                                    </div>
                                                  </div>

                                                  <div className="d-flex gap-2 align-items-center scheduler-theme__patient-actions">
                                                    <CTooltip content="Discharge Patient" placement="top">
                                                      <span className="scheduler-theme__icon-action" onClick={() => setDischargingPatient(patient)}>
                                                        <LogOutIcon size={15} />
                                                      </span>
                                                    </CTooltip>
                                                    <CTooltip content="Patient Information" placement="top">
                                                      <div
                                                        onClick={() => {
                                                          setShowPatientInfo(true)
                                                          setPatientInfo(patient)
                                                        }}
                                                        className="scheduler-theme__icon-action"
                                                      >
                                                        <Info size={15} />
                                                      </div>
                                                    </CTooltip>
                                                    <CTooltip content="Edit patient info" placement="top">
                                                      <span className="scheduler-theme__icon-action" onClick={() => handleAddPatientData(facility, patient)}>
                                                        <Pencil size={15} />
                                                      </span>
                                                    </CTooltip>
                                                  </div>
                                                </CCallout>
                                              )

                                              if (appointmentDetails.length > 0) {
                                                scheduled.push(render)
                                              } else {
                                                unscheduled.push(render)
                                              }
                                            })

                                            return (
                                              <>
                                                <div className="d-flex justify-content-end mb-2">
                                                  <div className="scheduler-theme__search-wrap">
                                                    <input
                                                      type="text"
                                                      placeholder={`Search Patient`}
                                                      value={searchTerms[facility.ID] || ''}
                                                      onChange={(e) => handleSearchChange(facility.ID, e.target.value)}
                                                      className="scheduler-theme__search-input"
                                                    />

                                                    <Search size={16} className="scheduler-theme__search-icon" />
                                                  </div>
                                                </div>

                                                {unscheduled}
                                                {scheduled}
                                              </>
                                            )
                                          })()
                                        ) : (
                                          <div className="text-center text-muted scheduler-theme__empty">
                                            <strong>No patients available for {facility?.Name}</strong>
                                          </div>
                                        )}
                                      </div>
                                    }
                                  />
                                </div>
                              )
                            })
                          )}
                        </div>
                      }
                      type={'facility'}
                    />
                  </div>
                )
              })}
            </div>
          </div>

          <div className="d-flex justify-content-center scheduler-theme__panel-footer">
            <CButton
              className="ui-action-btn ui-action-btn--primary scheduler-theme__cta"
              onClick={handleSchedule}
              disabled={createAppointmentLoader}
              aria-busy={createAppointmentLoader}
            >
              {createAppointmentLoader ? <CircularProgress size={18} color="inherit" /> : 'Create Appointments'}
            </CButton>
          </div>
        </div>

        {/* schedule appointments */}
        <div
          className="appointment-scheduler-panel d-flex flex-column px-2 border scheduler-theme__panel"
          style={{
            flex: '1 1 0',
            minWidth: 0,
            padding: '10px',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <div className="flex-grow-1">
            {/* <div className="border-bottom pb-2 mb-3">
              <h6 className="fw-medium">Scheduled Appointments</h6>
            </div> */}
            <div className="appointment-scheduler-panel-header d-flex align-items-center justify-content-between rounded px-4 py-2 border scheduler-theme__panel-header" style={{ minHeight: '55px' }}>
              <div className="d-flex align-items-center gap-2">
                <span className="fw-semibold scheduler-theme__panel-title">Scheduled Appointments</span>
              </div>

              <div className="appointment-scheduler-stats d-flex align-items-center gap-2">
                {selectedProvider && (
                  <div className="d-inline-flex align-items-center gap-2 flex-nowrap">
                    <label htmlFor="facilityToggle" className="mb-0 scheduler-theme__label-muted fw-medium">
                      Scheduled Patients:
                    </label>
                    <span className="fw-bold">{scheduledForSelectedDateAndProvider?.length}</span>
                  </div>
                )}

                {selectedProvider && <div className="scheduler-theme__divider"></div>}

                {selectedProvider && (
                  <div className="d-flex align-items-center gap-2">
                    <span className="fw-medium scheduler-theme__label-muted">Provider Capacity:</span>
                    <span className="fw-bold">{getSafeInteger(selectedProvider?.Capacity?.Cap)}</span>
                  </div>
                )}
              </div>
            </div>

            <div
              className="px-1 mt-3 scheduler-theme__scroll"
              style={{
                flexGrow: 1,
                maxHeight: '57.5vh',
              }}
            >
              <UpdateSchedulerData
                appointments={onlyScheduled}
                allAppointments={appointments}
                selectedDate={selectedDate}
                selectedProvider={selectedProvider}
                patientData={patientData}
                accordionState={accordionState}
                setAccordionState={setAccordionState}
                handleSubmit={handleSubmit}
                handleReorderSubmit={handleReorderSubmit}
                setAppointments={setAppointments}
                originalAppointments={originalAppointments}
                getLatestData={getLatestData}
                formatDateFilter={formatDateFilter}
                handleDeleteAppointment={handleDeleteAppointment}
              />
            </div>
          </div>

          <div className="d-flex justify-content-center scheduler-theme__panel-footer">
            <CButton
              className="ui-action-btn ui-action-btn--primary scheduler-theme__cta"
              onClick={handleSave}
            >
              Save
            </CButton>
          </div>
        </div>

        <CModal
          backdrop="static"
          visible={showPatientInfo}
          onClose={() => {
            setShowPatientInfo(false)
            setPatientInfo({})
          }}
        >
          <CModalHeader className="dashboard-dark-modal-header">
            <CModalTitle>Patient Information</CModalTitle>
          </CModalHeader>
          <CModalBody className="dashboard-dark-modal-body">
            <div className="scheduler-theme__info-grid">
              <div>
                <strong>First Name:</strong> {patientInfo?.FirstName}
              </div>
              <div>
                <strong>Last Name:</strong> {patientInfo?.LastName}
              </div>

              <div>
                <strong>DOB:</strong> {patientInfo?.DOB}
              </div>
              <div>
                <strong>Age:</strong> {patientInfo?.Age}
              </div>

              <div>
                <strong>Gender:</strong> {patientInfo?.Gender}
              </div>
              <div>
                <strong>Room:</strong> {patientInfo?.Room}
              </div>
              <div>
                <strong>Status:</strong> {patientInfo?.Status}
              </div>
              <div>
                <strong>Facility:</strong> {patientInfo?.FacilityName}
              </div>
              <div>
                <strong>Diagnosis:</strong> {patientInfo?.Diagnosis || 'N/A'}
              </div>

              <div>
                <strong>Emergency Contact:</strong> {patientInfo?.EmergencyName}
              </div>
              <div>
                <strong>Emergency Phone:</strong> {patientInfo?.EmergencyPhone}
              </div>

              <div>
                <strong>Phone Number:</strong> {patientInfo?.PhoneNumber || 'N/A'}
              </div>
              {/* <div><strong>Address:</strong> {patientInfo?.Address || 'N/A'}</div> */}

              {/* <div><strong>City:</strong> {patientInfo?.City || 'N/A'}</div>
                            <div><strong>State:</strong> {patientInfo?.State || 'N/A'}</div>

                            <div><strong>Zip Code:</strong> {patientInfo?.ZipCode || 'N/A'}</div>
                            <div><strong>Country:</strong> {patientInfo?.Country || 'N/A'}</div> */}
            </div>
          </CModalBody>
        </CModal>

        <CModal
          backdrop="static"
          visible={showAvailableProvider}
          onClose={() => {
            setShowAvailableProvider(false)
            setAvailableProviderForSelFacility([])
          }}
        >
          <CModalHeader className="dashboard-dark-modal-header">
            <CModalTitle>Available Provider</CModalTitle>
          </CModalHeader>
          <CModalBody className="dashboard-dark-modal-body">
            {Array.isArray(availableProviderForSelFacility) && availableProviderForSelFacility.length > 0 ? (
              <div className="d-flex flex-column gap-2">
                {availableProviderForSelFacility.map((provider) => (
                  <div key={provider.ID} className="scheduler-theme__provider-card">
                    {provider.Name}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-muted text-center">No available providers found.</div>
            )}
          </CModalBody>
        </CModal>

      </div>
      <CModal className="dashboard-dark-modal" visible={dischargingPatient} onClose={() => setDischargingPatient(null)} aria-labelledby="LiveDemoExampleLabel">
        <CModalHeader className="dashboard-dark-modal-header">
          <CModalTitle id="LiveDemoExampleLabel">Discharge patient</CModalTitle>
        </CModalHeader>
        <CModalBody className="dashboard-dark-modal-body">Do you really want to discharge this patient?</CModalBody>
        <CModalFooter className="dashboard-dark-modal-footer">
          <CButton className="dashboard-dark-modal-button" onClick={() => setDischargingPatient(null)}>
            Cancel
          </CButton>
          <CButton className="dashboard-dark-modal-button dashboard-dark-modal-button--primary" onClick={handleDischargePatient}>
            Discharge Patient
          </CButton>
        </CModalFooter>
      </CModal>
      {/* patient edit */}
      <CModal
        size="lg"
        visible={modalVisible}
        className="dashboard-dark-modal"
      // onClose={resetStates}
      >
        <CModalHeader className="dashboard-dark-modal-header">
          <CModalTitle>Edit Patient</CModalTitle>
        </CModalHeader>

        <CModalBody className="dashboard-dark-modal-body">
          <div className="m-3">
            <EditPatientDetails formData={childformData} setFormData={setChildFormData} facility={editPatientFacility} setFacility={setEditPatientFacility} />
          </div>
        </CModalBody>
        <CModalFooter className="dashboard-dark-modal-footer">
          <CButton
            onClick={() => {
              setModalVisible(false)
            }}
            color="secondary"
            variant="outline"
          >
            Close
          </CButton>
          <CButton color="primary" disabled={creatingPatientLoader} onClick={() => handlePatientSubmit()}>
            {creatingPatientLoader ? <CircularProgress className="text-white p-1" size={20} /> : 'Edit'}
          </CButton>
        </CModalFooter>
      </CModal>

      <Pageloader isLoading={loader} />

    </div>
  )
}

export default SchedulePatient2
